/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable prefer-destructuring */

import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { Form, message } from 'antd';
import apiRoutes from 'config/apiRoute';
import appRoutes from 'config/appRoutes';
import apiRequests from 'utils/api';
import asyncErrorHandler from 'utils/asyncErrorHandler';
import { updateAuthData } from 'store/container/auth/auth-slice';
import { parseInterestsToFormFields } from './interestFields';

const useSingleContact = (uuidOverride?: string) => {
  const user = useSelector((store: any) => store.auth.user);
  const history = useHistory();
  const params: any = useParams();
  const uuid = uuidOverride || params?.uuid;
  const dispatch = useDispatch();
  const isCreatePage = !uuid;

  const [state, setState] = useState({
    loading: !isCreatePage,
    data: null as any,
    error: true,
    fetchError: null as any,
    saving: false,
  });

  const [form] = Form.useForm();

  const isAuthUser = user.contact.uuid === uuid;
  let authUserRole = '';

  if (user.is_super) {
    authUserRole = 'super_user';
  } else {
    authUserRole = user.contact.type;
  }

  const parseDataForForm = (data: any) => {
    if (data?.data) {
      const contactData = data.data;
      const contactCustomer = contactData.customer;

      const nameParts = (contactData.name || '').trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      const userData = contactData.user || {};
      const userResume = userData.resume || {
        basics: {
          summary: '',
          birth_date: '',
          profiles: [],
        },
        interests: [],
        work: [],
      };

      const workData =
        userResume.work && userResume.work.length > 0
          ? userResume.work
          : [
              {
                name: contactCustomer.business_name,
                position: contactData.job_title,
                position_description: '',
                url: '',
                startDate: '',
                endDate: '',
                summary: '',
                highlights: [],
              },
            ];

      const interestFields = parseInterestsToFormFields(userResume.interests || []);

      const newObj = {
        ...contactData,
        ...interestFields,
        first_name: firstName,
        last_name: lastName,
        user: {
          ...userData,
          resume: {
            ...userResume,
            work: workData,
          },
        },
      };

      if (contactData.user?.preferences) {
        const p = contactData.user.preferences;
        newObj.user = {
          ...newObj.user,
          preferences: {
            email_notifications: p.email_notifications === '1',
            sms_notifications: p.sms_notifications === '1',
            push_notifications: p.push_notifications === '1',
            auto_follow_tasks: p.auto_follow_tasks === '1',
          },
        };
      }

      if (newObj?.customers && Array.isArray(newObj?.customers)) {
        newObj.customers = newObj?.customers?.map((el: any) => `${el?.uuid}`);
      }

      newObj.status = newObj.status === 'active';

      if (isAuthUser) {
        newObj.preferences = {};

        Object.keys(newObj.user.preferences).forEach((key) => {
          if (newObj.user.preferences[key] === null) {
            newObj.preferences[key] = null;
          } else {
            newObj.preferences[key] = newObj.user.preferences[key] === '1';
          }
        });
      }

      if (newObj.user) {
        newObj.weekly_hours_contract = newObj.user.weekly_hours_contract;
        const serverClockIn = newObj.user.usual_clock_in;
        if (serverClockIn) {
          const parts = serverClockIn.split(':');
          const h = String(parseInt(parts[0], 10)).padStart(2, '0');
          const m = parts[1] || '00';
          newObj.usual_clock_in = `${h}:${m}`;
        } else {
          newObj.usual_clock_in = '00:00';
        }
        const serverClockOut = newObj.user.usual_clock_out;
        if (serverClockOut) {
          const parts = serverClockOut.split(':');
          const h = String(parseInt(parts[0], 10)).padStart(2, '0');
          const m = parts[1] || '00';
          newObj.usual_clock_out = `${h}:${m}`;
        } else {
          newObj.usual_clock_out = '00:00';
        }
        newObj.is_super = newObj.user.is_super;
        newObj.manager_id = newObj.manager?.uuid || null;
      }

      return newObj;
    }

    return {};
  };

  const handleUpdateContact = async (data: any) => {
    try {
      setState((prevState) => ({ ...prevState, saving: true }));
      if (data?.customers?.length) {
        data.customer_id = data.customers[0];
      }

      if (isCreatePage) {
        const res = await apiRequests.post(`${apiRoutes.CONTACTS}`, data);
        message.success('User created');
        history.push(`${appRoutes.CONTACTS}/${res.data.data.uuid}/edit`);
        return;
      }

      const res = await apiRequests.put(`${apiRoutes.CONTACTS}/${uuid}`, data);
      message.success('User updated');

      if (isAuthUser) {
        dispatch(updateAuthData({ ...user, contact: res.data.data }));
      }

      setState((prevState) => ({ ...prevState, data: res.data, saving: false }));
    } catch (error) {
      asyncErrorHandler(error);
      setState((prevState) => ({ ...prevState, saving: false }));
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setState((prevState) => ({ ...prevState, loading: true }));
        const res = await apiRequests.get(`${apiRoutes.CONTACTS}/${uuid}`);
        const newRes = res.data;
        newRes.data.roles_data = newRes.data.roles;
        newRes.data.roles = newRes.data.roles_data?.map((el: any) => el.id);

        form.setFieldsValue(parseDataForForm(newRes));

        setState((prevState) => ({
          ...prevState,
          data: res.data,
          loading: false,
        }));
      } catch (error) {
        asyncErrorHandler(error);
        setState((prevState) => ({ ...prevState, error: true, loading: false, fetchError: error }));
      }
    };

    if (!isCreatePage) {
      fetchData();
    }
  }, [uuid, dispatch, isCreatePage]);

  return {
    form,
    uuid,
    state,
    user,
    isCreatePage,
    isAuthUser,
    authUserRole,
    handleUpdateContact,
    parseDataForForm,
  };
};

export default useSingleContact;
